import {
  CustomerApplicationStatusEnum,
  LendingPaymentSource,
  Mutation,
  Query,
  Typenames,
} from '@varo/graphql-schema-types';
import { useRouter } from 'expo-router';
import { REFETCH_QUERY_INTERVAL } from 'features/advance/constants/constants';
import { useEffect, useRef, useState } from 'react';
import { SubmitAdvanceApplication } from 'services/graphql/mutations/Advance/SubmitAdvanceApplication';
import { ApplicationStatus as ApplicationStatusQuery } from 'services/graphql/queries/advance/ApplicationStatus';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { ErrorScreenType } from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import { getUuid } from 'shared/utils/getUuid/getUuid';
import { useMutation, useQuery } from 'urql';

const useSubmitApplication = () => {
  const router = useRouter();
  const [shouldRefreshIdempotencyId, setShouldRefreshIdempotencyId] =
    useState(false);
  const [showLoadingScreen, setShowLoadingScreen] = useState(false);
  const [applicationId, setApplicationId] = useState('');
  const [retries, setRetries] = useState(0);
  const [advanceStatus, setAdvanceStatus] =
    useState<CustomerApplicationStatusEnum | null>(null);
  const idempotencyId = useRef<string>(getUuid());

  const [
    {
      data: submitAdvanceData,
      fetching: submitAdvanceFetching,
      error: submitAdvanceError,
    },
    executeMutation,
  ] = useMutation<Mutation>(SubmitAdvanceApplication);

  const [
    { data: applicationData, fetching, error: applicationStatusError },
    refetch,
  ] = useQuery<Query>({
    query: ApplicationStatusQuery,
    requestPolicy: 'cache-and-network',
    pause: !applicationId,
    variables: { applicationStatusId: applicationId },
  });

  const executeSubmitAdvance = (
    advanceId: string,
    autoRepaymentDate: string,
    agreementIds: string[],
    selectedAccountId?: string,
    paymentSource?: LendingPaymentSource,
  ) => {
    const id = shouldRefreshIdempotencyId ? getUuid() : idempotencyId.current;
    setShouldRefreshIdempotencyId(false);

    const input = {
      advance: {
        advanceId,
        dueDate: autoRepaymentDate,
      },
      agreements: agreementIds,
      ...(selectedAccountId && paymentSource
        ? {
            autoPaymentInput: {
              paymentSource,
              paymentAccountId: selectedAccountId,
              autoPaymentDate: autoRepaymentDate,
            },
          }
        : {}),
    };

    void executeMutation({
      idempotencyId: id,
      input,
    });
  };

  const { defaultTitle, defaultMessage, cancelButtonTitle } =
    useDefaultErrorStrings();

  const buttons = {
    primaryButton: {
      title: cancelButtonTitle,
      onPress: () => router.replace('/'),
    },
  };

  const submitAdvanceErrorModel = submitAdvanceError
    ? {
        screenType: ErrorScreenType.Error,
        messageHeadline: submitAdvanceError?.name ?? defaultTitle,
        messageBody: submitAdvanceError.message ?? defaultMessage,
      }
    : null;

  const applicationStatusErrorModel = applicationStatusError
    ? {
        screenType: ErrorScreenType.Error,
        messageHeadline: applicationStatusError?.name ?? defaultTitle,
        messageBody: applicationStatusError.message ?? defaultMessage,
        ...buttons,
      }
    : null;

  useEffect(() => {
    if (submitAdvanceData) {
      switch (submitAdvanceData?.submitAdvanceApplication?.__typename) {
        case Typenames.SubmitAdvanceApplicationSuccess:
          setShouldRefreshIdempotencyId(true); // this seems unnecessary
          setApplicationId(
            submitAdvanceData.submitAdvanceApplication.applicationId,
          );
          break;
        case Typenames.SubmitAdvanceApplicationError:
        case Typenames.SubmitAdvanceApplicationValidationError:
        case Typenames.SubmitAdvanceApplicationMissingAgreement:
          setShouldRefreshIdempotencyId(true);
          router.push({
            pathname: '/varo-advance/error',
            params: {
              subcategory: 'va_application',
              error_type: ErrorScreenType.Error,
              error_description:
                applicationStatusErrorModel?.messageBody ??
                submitAdvanceErrorModel?.messageBody,
              step_name: 'va_application_submission',
            },
          });
          break;
        default:
          setShouldRefreshIdempotencyId(true);
          break;
      }
    }
  }, [
    applicationStatusErrorModel?.messageBody,
    submitAdvanceData,
    submitAdvanceError,
    submitAdvanceErrorModel?.messageBody,
    router,
  ]);

  useEffect(() => {
    if (applicationData) {
      const status = applicationData?.applicationStatus;
      switch (status) {
        case CustomerApplicationStatusEnum.Approved:
        case CustomerApplicationStatusEnum.InReview:
        case CustomerApplicationStatusEnum.Incomplete:
        case CustomerApplicationStatusEnum.ReadyToSubmit:
        case CustomerApplicationStatusEnum.Submitted:
        case CustomerApplicationStatusEnum.Unknown:
        case CustomerApplicationStatusEnum.Declined:
          setAdvanceStatus(status);
          setShowLoadingScreen(false);
          break;
        case CustomerApplicationStatusEnum.Pending:
          if (retries <= 5) {
            setShowLoadingScreen(true);
            setTimeout(() => {
              setRetries(retries + 1);
              refetch();
            }, REFETCH_QUERY_INTERVAL);
          } else {
            setAdvanceStatus(status);
            setShowLoadingScreen(false);
          }
          break;
      }
    }
  }, [applicationData, applicationData?.applicationStatus, refetch, retries]);

  useEffect(() => {
    setShowLoadingScreen(
      showLoadingScreen || fetching || submitAdvanceFetching,
    );
  }, [showLoadingScreen, fetching, submitAdvanceFetching]);

  return {
    fetching: showLoadingScreen,
    error: submitAdvanceErrorModel ?? applicationStatusErrorModel,
    executeMutation: executeSubmitAdvance,
    showLoadingScreen,
    advanceStatus,
  };
};

export default useSubmitApplication;
